import type { DependencyList, RefObject } from 'react'

import { useEffect, useLayoutEffect } from 'react'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

// Scroll-linked motion ported from the client's draft (GSAP + ScrollTrigger).
// Every animation runs inside gsap.matchMedia scoped to one element, so it is
// reverted on unmount and on breakpoint changes, and nothing runs at all for
// visitors who ask for reduced motion: the markup is always readable as rendered.

let registered = false

const register = (): void => {
    if (registered) return

    gsap.registerPlugin(ScrollTrigger)
    registered = true
}

const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect

export interface MotionTools {
    gsap: typeof gsap
    ScrollTrigger: typeof ScrollTrigger
    desktop: boolean
}

export const useMotion = (
    scope: RefObject<HTMLElement | null>,
    setup: (tools: MotionTools) => void,
    deps: DependencyList = []
): void => {
    useIsomorphicLayoutEffect(() => {
        const element = scope.current

        if (!element) return

        register()

        const media = gsap.matchMedia(element)

        media.add({
            desktop: '(min-width: 768px) and (prefers-reduced-motion: no-preference)',
            mobile: '(max-width: 767px) and (prefers-reduced-motion: no-preference)'
        }, (context) => {
            const { desktop, mobile } = context.conditions as { desktop: boolean, mobile: boolean }

            if (!desktop && !mobile) return

            setup({ gsap, ScrollTrigger, desktop })
        })

        // Fonts and late images can move things a little, so measure again once they settle.
        const timer = window.setTimeout(() => ScrollTrigger.refresh(), 300)

        document.fonts?.ready.then(() => ScrollTrigger.refresh())

        return () => {
            window.clearTimeout(timer)
            media.revert()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps)
}

// Splits copy into per-letter spans for the draft's brightening effects.
// The full sentence is always given to screen readers separately.
export const splitWords = (text: string): string[][] => text.split(' ').map(word => Array.from(word))
